import { AlertCircle, AlertTriangle, CheckCircle, Info } from "lucide-react"
import { SnackbarProvider } from "notistack"
import { ReactNode } from "react"
// material
import { Box, GlobalStyles } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { ColorSchema } from "./library/@types/theme"
// @types

// ----------------------------------------------------------------------

function SnackbarStyles() {
  const theme = useTheme()
  const isLight = theme.palette.mode === "light"

  return (
    <GlobalStyles
      styles={{
        "#root": {
          "& .SnackbarContent-root": {
            width: "100%",
            padding: theme.spacing(1.5),
            margin: theme.spacing(0.25, 0),
            boxShadow: theme.customShadows.z8,
            borderRadius: theme.shape.borderRadius,
            color: theme.palette.grey[isLight ? 0 : 800],
            backgroundColor: theme.palette.grey[isLight ? 900 : 0],
            "&.SnackbarItem-variantSuccess, &.SnackbarItem-variantError, &.SnackbarItem-variantWarning, &.SnackbarItem-variantInfo":
              {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.paper
              }
          },
          "& .SnackbarItem-message": {
            padding: "0 !important",
            fontWeight: theme.typography.fontWeightMedium
          },
          "& .SnackbarItem-action": {
            marginRight: 0,
            color: theme.palette.action.active,
            "& svg": { width: 20, height: 20 }
          }
        }
      }}
    />
  )
}

type SnackbarIconProps = {
  icon: React.ComponentType<{ size?: number | string; "aria-label"?: string }>
  color: ColorSchema
  ariaLabel: string
}

function SnackbarIcon({ icon: IconComponent, color, ariaLabel }: SnackbarIconProps) {
  return (
    <Box
      component="span"
      sx={{
        mr: 1.5,
        width: 40,
        height: 40,
        display: "flex",
        borderRadius: 1.5,
        alignItems: "center",
        justifyContent: "center",
        color: `${color}.main`,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.16)
      }}
    >
      <IconComponent size={24} aria-label={ariaLabel} />
    </Box>
  )
}

type NotiStackProviderProps = {
  children: ReactNode
}

export default function NotiStackProvider({ children }: NotiStackProviderProps) {
  return (
    <>
      <SnackbarStyles />

      <SnackbarProvider
        dense
        maxSnack={5}
        // preventDuplicate
        autoHideDuration={3000}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        iconVariant={{
          success: (
            <SnackbarIcon icon={CheckCircle} color="success" ariaLabel="Success notification" />
          ),
          error: <SnackbarIcon icon={Info} color="error" ariaLabel="Error notification" />,
          warning: (
            <SnackbarIcon icon={AlertTriangle} color="warning" ariaLabel="Warning notification" />
          ),
          info: (
            <SnackbarIcon icon={AlertCircle} color="info" ariaLabel="Information notification" />
          )
        }}
      >
        {children}
      </SnackbarProvider>
    </>
  )
}
